import {
    importGuideLocalizationFile,
    fetchLocalizationPublicObject,
    saveLocalizationPublicObject,
    deleteTranslatedStrings,
    fetchSupportedTranslationLanguages,
} from './guide-localization.utils';
import { GuideLocalization } from '@pendo/services/BuildingBlocks';
import get from 'lodash/get';
import invert from 'lodash/invert';
import isUndefined from 'lodash/isUndefined';

const languageMapping = GuideLocalization.getAllLanguagesMapping();
const { rtlLanguageMapping } = GuideLocalization;

export const GUIDE_TRANSLATION_COMPLETE = 'COMPLETE';

export const GUIDE_TRANSLATION_PARTIAL = 'PARTIAL';

export const GUIDE_TRANSLATION_UNTRANSLATED = 'UNTRANSLATED';

export const XLIFF_IMPORT_ERROR_MESSAGE = {
    10101: 'The translation file is an unsupported xliff version.',
    10102: 'The translation file contains an incorrect target language.  Please check that all target-language fields are set to the value: "%code"',
    10103: 'The translation file contains malformed XML.',
    10104: 'The translation file you are importing does not match the current guide or resource center.',
};

export function importLocalizationFile (subscriptionId, guideId, langCode, fileData) {
    if (!subscriptionId) return Promise.reject(TypeError(`Expected subscription ID, but received: ${subscriptionId}`));
    if (!guideId) return Promise.reject(TypeError(`Expected guide ID, but received: ${guideId}`));
    if (!langCode) return Promise.reject(TypeError(`Expected language code, but received: ${langCode}`));
    if (!(fileData instanceof File)) return Promise.reject(Error('fileData must be an instance of File'));

    return new Promise((resolve) => {
        const fileReader = new FileReader();

        fileReader.onload = () => {
            const parser = new DOMParser();
            const fileXML = parser.parseFromString(fileReader.result, 'application/xml');

            return resolve(
                importGuideLocalizationFile(subscriptionId, guideId, langCode, fileXML.documentElement.outerHTML),
            );
        };

        fileReader.readAsText(fileData);
    });
}

export function fetchAppLocalizationData (appId) {
    return fetchLocalizationPublicObject(appId).then((resp) => resp.data);
}

export async function fetchAiTranslationSupportedLanguages () {
    const resp = await fetchSupportedTranslationLanguages();

    return resp.data;
}

export function isLanguageSupportedForAiTranslation (langCode, sourceLangCode, aiSupportedLanguages) {
    const baseLanguageCode = langCode.split('-')[0];
    const baseSourceLanguageCode = sourceLangCode.split('-')[0];

    if (baseLanguageCode === baseSourceLanguageCode) return false;

    return (aiSupportedLanguages ?? []).some(
        (language) => language.supportTarget && language.languageCode.split('-')[0] === baseLanguageCode,
    );
}

export function formatEnabledLanguages (localizationPulbicObject, includeUnmappedLanguages = false) {
    const { defaultLanguage, languageMappings: appEnabledLanguages } = localizationPulbicObject;
    const rtlLanguages = Object.keys(rtlLanguageMapping);

    return Object.entries(appEnabledLanguages).reduce((formattedLanguages, [metadata, code]) => {
        const sharedServiceLanguageMap = get(languageMapping, `${code}`);
        const isRtl = rtlLanguages.includes(code);
        if (!isUndefined(sharedServiceLanguageMap) || includeUnmappedLanguages) {
            formattedLanguages.push({
                code,
                // coercing to Bool since isDeprecated can be undefined
                isDeprecated: !!get(sharedServiceLanguageMap, 'isDeprecated'),
                name: get(sharedServiceLanguageMap, 'name'),
                nativeName: get(sharedServiceLanguageMap, 'nativeName'),
                isDefaultLanguage: code === defaultLanguage,
                metadataValue: metadata,
                ...(isRtl && { isRtl }),
            });
        }

        return formattedLanguages;
    }, []);
}

export function formatLanguages (appLocalizationData, guideTranslationStates) {
    return sortLanguagesByName(
        formatEnabledLanguages(appLocalizationData).map((language) => {
            language.translatedState = get(guideTranslationStates, `${language.code}.state`, null);

            return language;
        }),
    );
}

export function sortLanguagesByName (languages, isDefaultLangFirst = false) {
    let sortedLanguages = [].concat(languages);
    sortedLanguages = sortedLanguages.sort((a, b) => {
        if (a.name < b.name) return -1;
        if (a.name > b.name) return 1;

        return 0;
    });

    if (!isDefaultLangFirst) return sortedLanguages;

    const defaultLang = sortedLanguages.find((lang) => lang.isDefaultLanguage);
    const defaultLangIndex = sortedLanguages.indexOf(defaultLang);
    sortedLanguages.splice(defaultLangIndex, 1);
    sortedLanguages.splice(0, 0, defaultLang);

    return sortedLanguages;
}

export function updateLanguageMetadataValue (appId, { code, metadataValue }) {
    if (!appId) return Promise.reject(Error('Expected parameter {appId} but it was empty'));
    if (!code) return Promise.reject(Error('Expected parameter {code} but it was empty'));
    if (!metadataValue) return Promise.reject(Error('Expected parameter {metadataValue} but it was empty'));

    return fetchLocalizationPublicObject(appId).then(({ data: localizationConfig }) => {
        updateLanguageMappings(localizationConfig, code, metadataValue);

        return saveLocalizationPublicObject(appId, localizationConfig);
    });
}

export function updateLanguageMappings (localizationConfig, code, metadataValue) {
    const codeAsKeyMap = invert(localizationConfig.languageMappings);
    codeAsKeyMap[code] = metadataValue;
    localizationConfig.languageMappings = invert(codeAsKeyMap);
}

export function updateConfigProperty (appId, props = {}) {
    if (!appId) return Promise.reject(Error('Expected parameter {appId} but it was empty'));

    return fetchAppLocalizationData(appId).then((localizationConfig) => {
        Object.assign(localizationConfig, props);

        return saveLocalizationPublicObject(appId, localizationConfig).then((response) => response.data);
    });
}

export function clearTranslatedStrings (guideId, langCode) {
    if (!guideId) return Promise.reject(TypeError(`Expected guide ID, but received: ${guideId}`));
    if (!langCode) return Promise.reject(TypeError(`Expected language code, but received: ${langCode}`));

    return deleteTranslatedStrings(guideId, langCode);
}

const GuideLocalizationService = {
    GUIDE_TRANSLATION_COMPLETE,
    GUIDE_TRANSLATION_PARTIAL,
    GUIDE_TRANSLATION_UNTRANSLATED,
    importLocalizationFile,
    fetchAppLocalizationData,
    fetchAiTranslationSupportedLanguages,
    isLanguageSupportedForAiTranslation,
    formatEnabledLanguages,
    sortLanguagesByName,
    updateLanguageMappings,
    updateLanguageMetadataValue,
    updateConfigProperty,
    clearTranslatedStrings,
};

export default GuideLocalizationService;
